import { useEffect, useState } from "react";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";
import {
  adminDeactivate,
  SearchAdminByParams,
  sendAdminInvite,
  sendAdminInviteCoach,
} from "../../../services/AdminService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  adminSearchAction,
  removeAdminUserFromList,
  resetAdminState,
} from "../../../store/slices/adminSlice";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ConfirmModal from "../../../components/ConfirmModel";
import GraduatedClass from "./GraduatedClass";
import AdminInviteForm from "./AdminInviteForm";
import AdminUsersSearch from "./AdminUsersSearch";
import AdminUsersTable from "./AdminUsersTable";

export const LeftSide = () => {
  const [loading, setLoading] = useState(null);
  const [loadingInvited, setLoadingInvited] = useState(false);
  const [listAdmin, setListAdmin] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [errorSearch, setErrorSearch] = useState("");

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 480,
    tabletOffset: 480,
    desktopOffset: 480,
  });

  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();
  const r_admin = useSelector((state) => state.admin);
  const { adminUsers } = r_admin;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const handleInvite = async (email) => {
    setLoading(true);
    try {
      await sendAdminInvite(apiInstance, email);
      const newAdmin = { email: email };
      setListAdmin((prevList) => [...(prevList ?? []), newAdmin]);
      dispatch(adminSearchAction([...adminUsers, newAdmin]));
      toast.success("Invitation sent successfully!");
      return true;
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "An unknown error occurred.";
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleCoachInvite = async (coachEmail) => {
    setLoadingInvited(true);
    try {
      await sendAdminInviteCoach(apiInstance, coachEmail);
      toast.success("Coach invitation sent successfully!");
      return true;
    } catch (err) {
      const errorMessage =
        err?.error ||
        err?.response?.data?.error ||
        err.message ||
        "An unknown error occurred.";
      toast.error(errorMessage);
      return false;
    } finally {
      setLoadingInvited(false);
    }
  };

  const handleEmailChange = () => {
    dispatch(resetAdminState());
  };

  const handleSearchAdmin = (value) => {
    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue) {
      const result = adminUsers.filter((item) =>
        item.email?.toLowerCase().includes(normalizedValue),
      );
      setListAdmin(result);
    } else {
      setListAdmin(adminUsers);
    }
  };

  const deactivateAdmin = (userId) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUserId) {
      setLoading(true);
      try {
        await adminDeactivate(apiInstance, selectedUserId);
        dispatch(removeAdminUserFromList(selectedUserId));
        toast.success("Admin deactivated successfully!");
      } catch (err) {
        console.error("There was a problem with the request:", err);
        toast.error("Failed to deactivate admin.");
      } finally {
        setLoading(false);
        setShowDeleteModal(false);
        setSelectedUserId(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedUserId(null);
  };

  useEffect(() => {
    if (adminUsers) {
      setListAdmin(adminUsers);
    }
  }, [adminUsers]);

  useEffect(() => {
    const loadAdminUsers = async () => {
      setLoadingSearch(true);
      try {
        const data = await SearchAdminByParams(apiInstance, "");
        dispatch(adminSearchAction(data));
      } catch (err) {
        const errorMessage = err.message || "An unknown error occurred.";
        setErrorSearch(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoadingSearch(false);
      }
    };
    loadAdminUsers();
  }, [dispatch, apiInstance]);

  return (
    <>
      <div className="bg-white shadow-lg py-4 rounded-lg border-gray-300 h-full">
        <div className="w-full px-4">
          <h1 className="text-xl mb-4 uppercase">Admin Users</h1>
          <GraduatedClass />
          <AdminInviteForm
            label="Admin Email Address"
            name="email"
            initialValue=""
            onChange={handleEmailChange}
            onSubmitEmail={handleInvite}
            placeholder="newadmin@trackit.com"
            buttonText="Invite Admin"
            loading={loading}
          />
          <AdminInviteForm
            label="Coach Email Address"
            name="coachEmail"
            initialValue=""
            onSubmitEmail={handleCoachInvite}
            placeholder="coach@trackit.com"
            buttonText="Invite Coach"
            loading={loadingInvited}
          />
          <AdminUsersSearch onSearchChange={handleSearchAdmin} />
          {loadingSearch && <LoadingSpinner />}
          {errorSearch && <p className="text-red-500">Error: {errorSearch}</p>}
          {!loadingSearch && (
            <div className="w-full">
              <AdminUsersTable
                admins={listAdmin}
                adjustedHeight={adjustedHeight}
                onDeactivate={deactivateAdmin}
              />
            </div>
          )}
        </div>
      </div>
      <ConfirmModal
        show={showDeleteModal}
        message="Are you sure you want to delete this administrator?"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};
